import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ArrowLeft, CircleCheck, Clipboard, Loader2, UserPlus } from "lucide-react";
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { createInvoiceIn1C, previewInvoice } from "@/api/invoices";
import { createKontragentIn1C } from "@/api/mappings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatRub, formatRuDate } from "@/lib/format";
import { copyToClipboard, formatPartyForCopy } from "@/lib/partyFormat";
import { KontragentPicker } from "@/pages/invoices/KontragentPicker";
import { NomenclaturePicker } from "@/pages/invoices/NomenclaturePicker";
import {
  ResolveStatusBadge,
  statusTone,
} from "@/pages/invoices/ResolveStatusBadge";
import type {
  CreatedDocumentView,
  LineResolve,
  OneCRef,
  ParsedParty,
  ResolvedRef,
} from "@/types/api";
import { useConnection } from "@/auth/ConnectionContext";

export function InvoicePreviewPage(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const query = useQuery({
    queryKey: ["invoices", id, "preview", connId],
    queryFn: () => previewInvoice(id),
    retry: false,
  });

  const [kontragentPickerOpen, setKontragentPickerOpen] = useState(false);
  const [sellerPickerOpen, setSellerPickerOpen] = useState(false);
  const [linePickerIdx, setLinePickerIdx] = useState<number | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createInvoiceIn1C(id),
    onSuccess: (created) => {
      const num = created.postuplenie_number ?? "—";
      if (created.posted) {
        toast.success(`Документ № ${num} создан и проведён в 1С`);
      } else {
        toast.warning(
          `Документ № ${num} создан, но не проведён. Откройте его в 1С и проведите вручную.`,
        );
      }
      queryClient.invalidateQueries({ queryKey: ["invoices", id, "preview", connId] });
      queryClient.invalidateQueries({ queryKey: ["invoices", connId] });
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.detail ?? "Не удалось создать документ");
      } else {
        toast.error("Не удалось создать документ");
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/invoices">
            <ArrowLeft className="size-4" />К списку
          </Link>
        </Button>
      </div>

      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      )}
      {query.isError && (
        <ErrorCard error={query.error} />
      )}

      {query.data && (
        <>
          <div>
            <h1 className="text-3xl font-bold">
              {query.data.document_number
                ? `Счёт-фактура № ${query.data.document_number}`
                : query.data.filename}
            </h1>
            {query.data.document_date && (
              <p className="text-muted-foreground">
                от {formatRuDate(query.data.document_date)}
                {query.data.total_with_vat && (
                  <> · {formatRub(query.data.total_with_vat)}</>
                )}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Файл: <span className="font-mono">{query.data.filename}</span>
            </p>
          </div>

          {query.data.status === "error" && (
            <Card>
              <CardHeader>
                <CardTitle className="text-destructive">
                  Не удалось разобрать файл
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs text-muted-foreground">
                  {query.data.parse_error ?? "Неизвестная ошибка"}
                </pre>
              </CardContent>
            </Card>
          )}

          {query.data.status === "parsed" && query.data.created_document && (
            <CreatedDocumentBanner created={query.data.created_document} />
          )}

          {query.data.status === "parsed" && (
            <>
              <ResolvedPartyCard
                title="Организация (покупатель)"
                subtitle="Пойдёт в «Организация» документа поступления в 1С"
                ref_={query.data.organization}
                onChange={undefined}
                notFoundHint="Добавьте вашу организацию в 1С и обновите страницу."
                invoiceId={id}
              />

              <ResolvedPartyCard
                title="Контрагент"
                subtitle="Пойдёт в «Контрагент» документа поступления (шапка + договор)"
                ref_={query.data.kontragent}
                onChange={
                  query.data.kontragent.status === "multiple" ||
                  query.data.kontragent.status === "not_found"
                    ? () => setKontragentPickerOpen(true)
                    : undefined
                }
                notFoundHint="Добавьте контрагента в 1С и обновите страницу."
                invoiceId={id}
              />

              <ResolvedPartyCard
                title="Продавец (в счёт-фактуре)"
                subtitle="Пойдёт в поле «Продавец» подчинённого «Счёт-фактура полученный»"
                ref_={query.data.seller_in_invoice}
                onChange={
                  query.data.seller_in_invoice.status === "multiple" ||
                  query.data.seller_in_invoice.status === "not_found"
                    ? () => setSellerPickerOpen(true)
                    : undefined
                }
                notFoundHint="Добавьте продавца в 1С и обновите страницу."
                invoiceId={id}
              />

              <Card>
                <CardHeader>
                  <CardTitle>Позиции номенклатуры</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Статус</TableHead>
                        <TableHead>Из документа</TableHead>
                        <TableHead className="w-24 text-right">Кол.</TableHead>
                        <TableHead className="w-20">НДС</TableHead>
                        <TableHead>Номенклатура 1С</TableHead>
                        <TableHead className="w-32" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {query.data.lines.map((line, idx) => (
                        <LineRow
                          key={line.line_no}
                          line={line}
                          onPick={() => setLinePickerIdx(idx)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {!query.data.created_document && (
                <div className="flex items-center justify-end gap-3 rounded-md border bg-muted/30 p-4">
                  <div className="flex-1 text-sm text-muted-foreground">
                    {query.data.all_green ? (
                      <span className="text-green-700">
                        ✓ Все справочники сопоставлены — документ готов к созданию в 1С
                      </span>
                    ) : (
                      <span>
                        Чтобы создать документ, нужно сопоставить все жёлтые и
                        красные блоки
                      </span>
                    )}
                  </div>
                  <Button
                    disabled={!query.data.all_green || createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                  >
                    {createMutation.isPending
                      ? "Создание…"
                      : "Создать документ в 1С"}
                  </Button>
                </div>
              )}

              {/* Pickers */}
              <KontragentPicker
                open={kontragentPickerOpen}
                invoiceId={id}
                sourceInn={query.data.kontragent.source?.inn ?? ""}
                sourceKpp={null}
                sourceName={
                  query.data.kontragent.source?.name ?? "Контрагент"
                }
                suggestions={query.data.kontragent.suggestions}
                onClose={() => setKontragentPickerOpen(false)}
              />
              <KontragentPicker
                open={sellerPickerOpen}
                invoiceId={id}
                sourceInn={query.data.seller_in_invoice.source?.inn ?? ""}
                sourceKpp={null}
                sourceName={
                  query.data.seller_in_invoice.source?.name ??
                  [
                    query.data.seller_in_invoice.source?.last_name,
                    query.data.seller_in_invoice.source?.first_name,
                    query.data.seller_in_invoice.source?.middle_name,
                  ]
                    .filter(Boolean)
                    .join(" ") ??
                  "Продавец"
                }
                suggestions={query.data.seller_in_invoice.suggestions}
                onClose={() => setSellerPickerOpen(false)}
              />
              {linePickerIdx !== null && query.data.lines[linePickerIdx] && (
                <NomenclaturePicker
                  open={linePickerIdx !== null}
                  invoiceId={id}
                  sourceName={query.data.lines[linePickerIdx]!.source_name}
                  suggestions={query.data.lines[linePickerIdx]!.nomenclature.suggestions}
                  kontragentRefKey={
                    query.data.kontragent.selected?.ref_key ?? ""
                  }
                  organizationRefKey={
                    query.data.organization.selected?.ref_key ?? ""
                  }
                  onClose={() => setLinePickerIdx(null)}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function ErrorCard({ error }: { error: unknown }): React.ReactElement {
  let detail = "Неизвестная ошибка";
  let hint: string | null = null;
  if (error instanceof AxiosError) {
    detail = error.response?.data?.detail ?? error.message;
    const code = error.response?.status;
    if (code === 400) hint = "Перейдите в Профиль и настройте подключение к 1С.";
    else if (code === 401)
      hint = "Проверьте учётные данные 1С в профиле.";
    else if (code === 502 || code === 504) hint = "1С сейчас недоступна.";
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-destructive">Не удалось загрузить</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p>{detail}</p>
        {hint && <p className="text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function ResolvedPartyCard({
  title,
  subtitle,
  ref_,
  onChange,
  notFoundHint,
  invoiceId,
}: {
  title: string;
  subtitle?: string;
  ref_: ResolvedRef;
  onChange?: () => void;
  notFoundHint: string;
  invoiceId?: string;
}): React.ReactElement {
  const tone = statusTone(ref_.status);
  const borderClass =
    tone === "green"
      ? "border-green-500/40"
      : tone === "amber"
        ? "border-amber-500/40"
        : "border-destructive/40";

  return (
    <Card className={cn(borderClass)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          {title}
          <ResolveStatusBadge
            status={ref_.status}
            fromMapping={ref_.from_mapping}
          />
        </CardTitle>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {ref_.selected && (
          <div>
            <div className="font-medium">{ref_.selected.name}</div>
            {ref_.selected.inn && (
              <div className="text-xs text-muted-foreground">
                ИНН {ref_.selected.inn}
                {ref_.selected.kpp ? ` / КПП ${ref_.selected.kpp}` : ""}
              </div>
            )}
            <SourceReqsLine
              source={ref_.source}
              selected={ref_.selected}
            />
          </div>
        )}
        {ref_.status === "multiple" && (
          <div className="space-y-2">
            {ref_.source && (
              <div className="text-xs text-muted-foreground">
                Из файла: {ref_.source.name ?? "—"}
                {ref_.source.inn && <> · ИНН {ref_.source.inn}</>}
                {ref_.source.kpp && <> / КПП {ref_.source.kpp}</>}
              </div>
            )}
            <p className="text-muted-foreground">
              В 1С найдено {ref_.suggestions.length} совпадений. Выберите один.
            </p>
            {onChange && (
              <Button size="sm" variant="outline" onClick={onChange}>
                Выбрать из 1С
              </Button>
            )}
          </div>
        )}
        {ref_.status === "not_found" && (
          <NotFoundDetails
            source={ref_.source}
            hint={notFoundHint}
            onManualPick={onChange}
            invoiceId={invoiceId}
          />
        )}
      </CardContent>
    </Card>
  );
}

function CreatedDocumentBanner({
  created,
}: {
  created: CreatedDocumentView;
}): React.ReactElement {
  return (
    <Card className="border-green-500/50 bg-green-500/5">
      <CardContent className="flex items-start gap-3 p-4">
        <CircleCheck className="mt-0.5 size-5 text-green-700" />
        <div className="flex-1 space-y-1 text-sm">
          <div className="font-medium text-green-800">Документ создан в 1С</div>
          <div>
            Поступление №{" "}
            <span className="font-mono">
              {created.postuplenie_number ?? "—"}
            </span>
            {created.postuplenie_date && (
              <> от {formatRuDate(created.postuplenie_date)}</>
            )}
            {" · "}
            {created.posted ? (
              <span className="text-green-700">проведён</span>
            ) : (
              <span className="text-amber-700">не проведён</span>
            )}
          </div>
          {created.schet_faktura_number && (
            <div>
              Счёт-фактура №{" "}
              <span className="font-mono">
                {created.schet_faktura_number}
              </span>
            </div>
          )}
          {!created.posted && (
            <p className="text-xs text-muted-foreground">
              Документ создан без проведения — откройте его в 1С и проведите
              вручную.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SourceReqsLine({
  source,
  selected,
}: {
  source: ParsedParty | null;
  selected: OneCRef;
}): React.ReactElement | null {
  if (!source || !source.inn) return null;
  // Hide when nothing new to show (full match).
  const sameInn = source.inn === selected.inn;
  const sameKpp = (source.kpp ?? null) === (selected.kpp ?? null);
  if (sameInn && sameKpp) return null;
  return (
    <div className="mt-1 text-xs text-muted-foreground/80">
      В файле: ИНН {source.inn}
      {source.kpp ? ` / КПП ${source.kpp}` : ""}
    </div>
  );
}

function NotFoundDetails({
  source,
  hint,
  onManualPick,
  invoiceId,
}: {
  source: ParsedParty | null;
  hint: string;
  onManualPick?: () => void;
  invoiceId?: string;
}): React.ReactElement {
  if (!source || (!source.name && !source.inn && !source.last_name)) {
    return (
      <div className="space-y-2">
        <p className="text-muted-foreground">{hint}</p>
        {onManualPick && (
          <Button size="sm" variant="outline" onClick={onManualPick}>
            Найти в 1С вручную
          </Button>
        )}
      </div>
    );
  }

  const displayName =
    source.kind === "individual"
      ? [source.last_name, source.first_name, source.middle_name]
          .filter(Boolean)
          .join(" ") || "—"
      : source.name || "—";

  const handleCopy = async () => {
    const text = formatPartyForCopy(source);
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) toast.success("Скопировано");
    else toast.error("Не удалось скопировать");
  };

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground">
        В 1С не найдено. Создайте запись со следующими реквизитами:
      </p>
      <div className="rounded-md border bg-muted/30 p-3 text-xs">
        <div className="font-medium text-foreground">{displayName}</div>
        <div className="mt-1 space-y-0.5 text-muted-foreground">
          {source.inn && <div>ИНН: {source.inn}</div>}
          {source.kpp && <div>КПП: {source.kpp}</div>}
          {source.address_text && (
            <div>Адрес: {source.address_text.replace(/\s+/g, " ").trim()}</div>
          )}
          {source.country_code && source.kind === "foreign" && (
            <div>Страна: {source.country_code}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <CreateKontragentButton source={source} invoiceId={invoiceId} />
        <Button size="sm" variant="outline" onClick={handleCopy}>
          <Clipboard className="size-3.5" />
          Скопировать реквизиты
        </Button>
        {onManualPick && (
          <Button size="sm" variant="outline" onClick={onManualPick}>
            Найти в 1С вручную
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        После создания обновите страницу — блок сам станет зелёным.
      </p>
    </div>
  );
}

function CreateKontragentButton({
  source,
  invoiceId,
}: {
  source: ParsedParty;
  invoiceId?: string;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [busy, setBusy] = useState(false);

  if (!source.inn) return null;

  const displayName =
    source.kind === "individual"
      ? [source.last_name, source.first_name, source.middle_name]
          .filter(Boolean)
          .join(" ") || source.name || "—"
      : source.name || "—";

  const handleCreate = async () => {
    setBusy(true);
    try {
      const isIp = source.kind === "individual";
      const result = await createKontragentIn1C({
        inn: source.inn!,
        kpp: source.kpp,
        name: displayName,
        full_name: source.name,
        legal_type: isIp ? "ФизическоеЛицо" : "ЮридическоеЛицо",
        is_individual_entrepreneur: isIp,
      });
      if (result.ok) {
        toast.success(`Контрагент ${displayName} создан в 1С`);
        if (invoiceId) {
          queryClient.invalidateQueries({
            queryKey: ["invoices", invoiceId, "preview", connId],
          });
        }
        queryClient.invalidateQueries({ queryKey: ["invoices", connId] });
        queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
      } else {
        toast.error(`Ошибка: ${result.error ?? "не удалось создать"}`);
      }
    } catch {
      toast.error("Не удалось создать контрагента");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size="sm" variant="default" onClick={handleCreate} disabled={busy}>
      {busy ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <UserPlus className="size-3.5" />
      )}
      Создать в 1С
    </Button>
  );
}

function LineRow({
  line,
  onPick,
}: {
  line: LineResolve;
  onPick: () => void;
}): React.ReactElement {
  return (
    <TableRow
      className={line.is_incomplete_mapping ? "bg-amber-500/5" : undefined}
    >
      <TableCell>
        <ResolveStatusBadge
          status={line.nomenclature.status}
          fromMapping={line.nomenclature.from_mapping}
        />
      </TableCell>
      <TableCell className="max-w-[240px]">
        <div className="truncate font-medium" title={line.source_name}>
          {line.source_name}
        </div>
      </TableCell>
      <TableCell className="text-right">{line.quantity}</TableCell>
      <TableCell>{line.vat_rate}</TableCell>
      <TableCell className="max-w-[240px]">
        {line.nomenclature.selected ? (
          <>
            <div
              className="truncate font-medium"
              title={line.nomenclature.selected.name}
            >
              {line.nomenclature.selected.name}
            </div>
            {line.is_incomplete_mapping ? (
              <div className="text-xs text-amber-700">
                ⚠️ Не выбран договор и счета — нажмите «Изменить»
              </div>
            ) : line.dogovor_name ? (
              <div
                className="truncate text-xs text-muted-foreground"
                title={line.dogovor_name}
              >
                Договор: {line.dogovor_name}
              </div>
            ) : null}
          </>
        ) : line.nomenclature.status === "multiple" ? (
          <span className="text-sm text-muted-foreground">
            Найдено {line.nomenclature.suggestions.length} вариантов
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button size="sm" variant="outline" onClick={onPick}>
          {line.nomenclature.selected ? "Изменить" : "Выбрать"}
        </Button>
      </TableCell>
    </TableRow>
  );
}
